<script setup lang="ts">
    import { ref, onMounted } from 'vue';
    import { useSearchStore } from '@/stores/search';
    import { useTheme } from 'vuetify';
    import { FeedLiveItem } from '@/models/feedLive';
    import { FeedPregameItem } from '@/models/feedPregame';
    import { dateFormatInUserTimeZone, getCountryFlagUrl } from '@/helpers';
    import { timeUntilEventKickoff } from '@/helpers/event';
    import { League } from '@/constants';
    import { eventService } from '@/services/eventService';
    import { useEventStore } from '@/stores/event';
    import { ISearchOptions } from '@/types';

    const loading = ref(false);
    const searchStore = useSearchStore();
    const theme = useTheme();
    const eventStore = useEventStore();
    const { t } = useI18n();
    const searchRef = useTemplateRef('searchRef');

    let _timerId: any;

    function updateSearchQuery(query: string, force: boolean = false) {
        searchStore.searchQuery = query;

        if (force) {
            loading.value = true;
            loadData();
            loading.value = false;
        } else {
            clearTimeout(_timerId);
            loading.value = true;

            _timerId = setTimeout(() => {
                if (query.trim().length >= 3 && query.trim().length < 50) {
                    loadData();
                }

                loading.value = false;
            }, 400);
        }
    }

    const loadData = async (options: ISearchOptions = {}) => {
        await eventService.search(searchStore.searchQuery, options);
        loading.value = false;
    };

    const clear = () => {
        searchStore.searchQuery = '';
        searchStore.items = [];
    };

    const showEventDialog = (item: FeedPregameItem | FeedLiveItem) => {
        eventStore.openEventDialog(item);
    };

    onMounted(() => {
        console.log('Component app-search mounted.');
        watch(
            () => searchStore.externalSearchQuery,
            async (externalSearchQuery) => {
                if (!externalSearchQuery) return;
                //need to clear items, otherwise list will be not opened after second external search
                searchStore.items = [];
                // searchStore.searchQuery = externalSearchQuery;
                //searchRef.value!.search = externalSearchQuery;
                //there is option to use isFocused = true, but maybe it is incorrect. @update:focused is not executed in this case
                //searchRef.value!.isFocused = true;
                searchRef.value!.isFocused = true;
                searchRef.value!.menu = true;
                searchRef.value!.isPristine = false;
                // const inputEl = searchRef.value!.$el.querySelector('input');
                // inputEl?.focus();

                //searchStore.externalSearchQuery = '';
                //need to use "exact" search here
                // await loadData({ exact: true });
                searchStore.externalSearchQuery = '';
                nextTick(async () => {
                    updateSearchQuery(externalSearchQuery, true);
                    //     searchRef.value!.search = externalSearchQuery;
                    //     /*const inputEl = searchRef.value?.$el.querySelector('input');
                    //     inputEl?.focus();*/
                    //     //searchRef.value!.isFocused = true;
                });
            }
        );
    });
</script>
<template>
    <div style="width: 250px" class="pr-2">
        <v-autocomplete
            autocomplete="off"
            ref="searchRef"
            variant="outlined"
            flat
            clearable
            density="compact"
            :loading="loading"
            :items="searchStore.items as (FeedLiveItem | FeedPregameItem)[]"
            hide-no-data
            :hide-details="true"
            :label="t('common.search')"
            :search="searchStore.searchQuery"
            @update:search="updateSearchQuery"
            @click:clear="clear"
            class="search"
            :no-filter="true"
            color="tbGreen"
            base-color="tbGreen"
            item-color="tbGreen"
            single-line
            persistent-clear
            hide-selected
            :menu-props="{ maxHeight: 479 }"
        >
            <template v-slot:prepend-inner>
                <v-icon :color="theme.name.value === 'dark' ? '' : 'tbGreen'" class="fullOpacity" size="24" :icon="mdiMagnify" />
            </template>

            <template v-slot:item="{ item }">
                <v-row
                    :class="item.raw.leagueId.value === League.Fantasy ? 'fantasy' : item.raw instanceof FeedLiveItem ? 'live' : 'pregame'"
                    class="py-1 ma-0 search-row font13 cursorPointer"
                    @click="showEventDialog(item.raw)"
                >
                    <v-col class="text-center py-0 px-1 my-auto" style="min-width: 36px; max-width: 36px">
                        <v-btn :disabled="item.raw.isEventProcessing()" icon tag="a" variant="text" @click.stop="item.raw.toggleEventFavorite()">
                            <v-icon size="20" :color="item.raw.isEventFavorite() ? 'favStarOn' : 'favStarOff'" :icon="item.raw.isEventFavorite() ? mdiStar : mdiStarOutline"></v-icon>
                        </v-btn>
                    </v-col>
                    <v-col cols="2" class="text-center pa-0" style="min-width: 83px; max-width: 83px">
                        <template v-if="item.raw instanceof FeedLiveItem && item.raw.scoreHome.value !== null && item.raw.time.value !== null">
                            <span :class="[item.raw.time.value === 'FT' ? 'ft-time' : 'time', 'bold']" v-html="item.raw.time.value"></span>
                            <br />
                            <span class="bold">
                                <span v-html="item.raw.scoreHome.value + ' - ' + item.raw.scoreAway.value" :class="[item.raw.time.value === 'FT' ? 'ft-score' : 'score']"></span>
                            </span>
                        </template>
                        <template v-else-if="item.raw instanceof FeedPregameItem">
                            <span class="grey--text">
                                {{ dateFormatInUserTimeZone(item.raw.eventStartedAt.value!, 'dd/MM H:mm') }}
                                <br />
                                <span class="greenText">{{ timeUntilEventKickoff(item.raw.leftUntilStart.value) }}</span>
                            </span>
                        </template>

                        <div v-else class="time bold font10 pt-2">{{ t('feed.scoreNotConfirmed') }}</div>
                    </v-col>
                    <v-col cols="1" class="text-center pa-0 d-flex flex-column align-center justify-center" style="min-width: 25px; max-width: 25px">
                        <span v-if="item.raw.leagueId.value !== League.Fantasy">
                            <img class="mb1" v-if="item.raw.iso3.value" :alt="item.raw.iso3.value" :src="getCountryFlagUrl(item.raw.iso3.value)" width="15" height="auto" />
                            <img class="mb1" v-else alt="world" :src="getCountryFlagUrl('world')" width="15" height="auto" />
                        </span>
                    </v-col>
                    <v-col>
                        <v-row :class="theme.name.value === 'dark' && item.raw.leagueId.value === League.Fantasy ? 'purpleText' : 'search-team'">
                            {{ item.raw.teamHomeName }}
                        </v-row>
                        <v-row :class="theme.name.value === 'dark' && item.raw.leagueId.value === League.Fantasy ? 'purpleText' : 'search-team'">
                            {{ item.raw.teamAwayName }}
                        </v-row>
                    </v-col>
                </v-row>
            </template>
        </v-autocomplete>
    </div>
</template>
<style scoped src="./style.css"></style>
